import { defineStore } from 'pinia'
import { modalDialogDefaults } from '~/cynqueModules/store/modalDialogState'

/**
 * Converted from store/modalDialog.js.
 *
 * ALMOST EVERY GETTER IS GONE, and deliberately: fourteen of the fifteen were
 * pure pass-throughs of a same-named state field. Vuex kept getters and state in
 * separate namespaces so the two could share a name; Pinia merges them onto one
 * object, where the getter shadows the field and makes it readonly — every
 * action's write would then fail SILENTLY. The state fields answer every caller
 * unchanged, because the facade resolves `modalDialog/title` against the whole
 * store, and a state field is on the store.
 *
 * `showModalDialog` loses a `=== true` coercion along with its getter. A no-op:
 * it defaults to false and its only writer is setShowModalDialog, which callers
 * pass a boolean.
 *
 * The one getter kept is `cancelButtonCaptionsVerbOverride` — note the "s",
 * which the state field does not have. Not a collision, so it stays as the
 * rename it has always been.
 */
export const useModalDialogStore = defineStore('modalDialog', {
  state: () => ({ ...modalDialogDefaults }),

  getters: {
    // Deliberately spelled differently from the field it returns.
    cancelButtonCaptionsVerbOverride: (state) =>
      state.cancelButtonCaptionVerbOverride,
  },

  actions: {
    setShowModalDialog(stateToSet: boolean): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating showModalDialog from ${this.showModalDialog} to ${stateToSet}`
      )
      this.showModalDialog = stateToSet
    },

    setTitle(title: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(`store: mutating title from '${this.title}' to '${title}'`)
      this.title = title
    },
    clearTitle(): void {
      this.setTitle(null)
    },

    setSubtitle(subtitle: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating subtitle from '${this.subtitle}' to '${subtitle}'`
      )
      this.subtitle = subtitle
    },
    clearSubtitle(): void {
      this.setSubtitle(null)
    },

    setMessage(message: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating message from '${this.message}' to '${message}'`
      )
      this.message = message
    },
    clearMessage(): void {
      this.setMessage(null)
    },

    setDialogStyle(dialogStyle: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating dialogStyle from '${this.dialogStyle}' to '${dialogStyle}'`
      )
      this.dialogStyle = dialogStyle
    },
    clearDialogStyle(): void {
      this.setDialogStyle(null)
    },

    // Named setDoShowCancelButton but writes doHideCancelButton — preserved as
    // it was; the caller-facing name is the one in use.
    setDoShowCancelButton(doHideCancelButton: boolean): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating doHideCancelButton from '${this.doHideCancelButton}' to '${doHideCancelButton}'`
      )
      this.doHideCancelButton = doHideCancelButton
    },
    clearDoShowCancelButton(): void {
      this.setDoShowCancelButton(false)
    },

    setOkShouldActLikeCancel(okShouldActLikeCancel: boolean): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating okShouldActLikeCancel from '${this.okShouldActLikeCancel}' to '${okShouldActLikeCancel}'`
      )
      this.okShouldActLikeCancel = okShouldActLikeCancel
    },
    clearOkShouldActLikeCancel(): void {
      this.setOkShouldActLikeCancel(false)
    },

    setOkButtonCaptionVerbOverride(override: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating okButtonCaptionVerbOverride from '${this.okButtonCaptionVerbOverride}' to '${override}'`
      )
      this.okButtonCaptionVerbOverride = override
    },
    clearOkButtonCaptionVerbOverride(): void {
      this.setOkButtonCaptionVerbOverride(null)
    },

    setCancelButtonCaptionVerbOverride(override: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating cancelButtonCaptionVerbOverride from '${this.cancelButtonCaptionVerbOverride}' to ${override}`
      )
      this.cancelButtonCaptionVerbOverride = override
    },
    clearCancelButtonCaptionVerbOverride(): void {
      this.setCancelButtonCaptionVerbOverride(null)
    },

    setDoSkipVerification(doSkipVerification: boolean): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating doSkipVerification from '${this.doSkipVerification}' to '${doSkipVerification}'`
      )
      this.doSkipVerification = doSkipVerification
    },
    clearDoSkipVerification(): void {
      this.setDoSkipVerification(false)
    },

    setOkButtonWidth(width: string | number | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating okButtonWidth from '${this.okButtonWidth}' to '${width}'`
      )
      this.okButtonWidth = width
    },
    clearOkButtonWidth(): void {
      this.setOkButtonWidth(null)
    },

    setDidClickOk(didClickOk: boolean): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating didClickOk from ${this.didClickOk} to ${didClickOk}`
      )
      this.didClickOk = didClickOk
    },
    clearDidClickOk(): void {
      this.setDidClickOk(false)
    },

    setIsProcessing(isProcessing: boolean): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating isProcessing from ${this.isProcessing} to ${isProcessing}`
      )
      this.isProcessing = isProcessing
    },
    clearIsProcessing(): void {
      this.setIsProcessing(false)
    },

    setOnOkCallback(onOkCallback: unknown): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating onOkCallback from ${this.onOkCallback} to ${onOkCallback}`
      )
      this.onOkCallback = onOkCallback
    },
    clearOnOkCallback(): void {
      this.setOnOkCallback(undefined)
    },

    setOnOkCallbackData(onOkCallbacData: unknown): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating onOkCallback from ${this.onOkCallbackData} to ${onOkCallbacData}`
      )
      this.onOkCallbackData = onOkCallbacData
    },
    clearOnOkCallbackData(): void {
      this.setOnOkCallbackData(undefined)
    },

    /**
     * NOT `$reset()`. This clears everything the Vuex chain cleared and, exactly
     * as before, leaves `didClickOk` and `isProcessing` ALONE — they are managed
     * around the OK-click lifecycle, and a blanket reset would clobber them
     * mid-flight. The hazard is documented on modalDialogDefaults; this is the
     * code it warns about.
     */
    async clearModalDialog(): Promise<void> {
      // eslint-disable-next-line no-console
      console.debug('store: running clearModalDialog...')
      this.setShowModalDialog(false)
      this.clearTitle()
      this.clearSubtitle()
      this.clearMessage()
      this.clearDialogStyle()
      this.clearDoShowCancelButton()
      this.clearOkShouldActLikeCancel()
      this.clearOkButtonCaptionVerbOverride()
      this.clearCancelButtonCaptionVerbOverride()
      this.clearDoSkipVerification()
      this.clearOkButtonWidth()
      this.clearOnOkCallback()
      this.clearOnOkCallbackData()
    },
  },
})
